import { PencilIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Avatar, Badge, Box, Flex, Text, type BadgeProps } from '@/components/common';
import './notes.css';

export type NoteCircleVariant = 'none' | 'outline' | 'dashed';

interface NoteCircleProps {
  name: string;
  photoUrl?: string;
  content?: string | null;
  color: NonNullable<BadgeProps['color']>;
  variant: NoteCircleVariant;
  highContrast?: boolean;
  sublabel?: ReactNode;
  onClick: () => void;
  showLiveLabel?: boolean;
  /** Corner action badge: + when empty, pencil when a note exists. */
  editable?: boolean;
}

function ActionBadge({ empty }: { empty: boolean }) {
  return (
    <Flex
      as="span"
      position="absolute"
      right="calc(-3px * var(--scaling))"
      bottom="calc(-3px * var(--scaling))"
      width="calc(22px * var(--scaling))"
      height="calc(22px * var(--scaling))"
      align="center"
      justify="center"
      data-accent-color="red"
      className="note-add-badge"
      aria-hidden
    >
      {empty ? (
        <PlusIcon size={14} strokeWidth={2.5} />
      ) : (
        <PencilIcon size={12} strokeWidth={2.5} />
      )}
    </Flex>
  );
}

export function NoteCircle({
  name,
  photoUrl,
  content,
  color,
  variant,
  highContrast = false,
  sublabel,
  onClick,
  showLiveLabel = false,
  editable = false,
}: NoteCircleProps) {
  const { t } = useTranslation('notes');
  const empty = content == null || content === '';
  const bubbleContent = empty ? t('circle.placeholder') : content;

  return (
    <button
      type="button"
      className="note-circle"
      onClick={onClick}
      aria-label={empty ? t('circle.addA11y') : t('circle.openA11y', { name })}
      data-accent-color={color === 'black' ? undefined : color}
      data-variant={variant}
    >
      <Flex direction="column" align="center" gap="2" minWidth="0" width="100%">
        <Badge
          color={color}
          highContrast={highContrast}
          radius="large"
          size="1"
          wrap="wrap"
          italic={empty}
          className="note-bubble"
        >
          {bubbleContent}
        </Badge>
        <Box
          as="span"
          position="relative"
          display="inline-block"
          data-accent-color={variant === 'none' ? undefined : 'red'}
          className="note-avatar-wrap"
        >
          <Avatar name={name} src={photoUrl} size="5" radius="full" />
          {editable && <ActionBadge empty={empty} />}
          {showLiveLabel && (
            <Badge color={color} radius="full" size="1" className="note-live-pill">
              {t('circle.live')}
            </Badge>
          )}
        </Box>
        <Flex direction="column" align="center" gap="0" minWidth="0" width="100%">
          <Text size="1" weight="bold" truncate wrap="nowrap" className="font-mono">
            {name}
          </Text>
          {sublabel}
        </Flex>
      </Flex>
    </button>
  );
}
